import React, { useMemo, useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { TrendingUp, Users, Wallet, Radio, MessageCircle, MessageSquare, RefreshCw, ArrowUpRight, Award, ChevronsRight, ChevronsLeft, Monitor, LayoutDashboard, Settings as SettingsIcon, Search, Send, Check, AlertTriangle, X, LogOut, Layers, Rocket, DoorOpen, Menu, UserCheck, Sparkles, Play, Square } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { adminApi } from "@/lib/authApi";
import { Panel, Eyebrow, MonoLabel, LiveDot, KPI } from "@/components/Panel";
import { MEMBERS, CATEGORIES, TIERS, REGISTRATION_TARGET, findMember, INTEGRATIONS, SEATING } from "@/data/seed";
import { useReferralsLive, setBigScreenSpotlight } from "@/lib/referralsApi";
import { useDemo } from "@/store/DemoStore";
import { AlertHistoryButton } from "@/components/AlertHistory";
import { EventPicker } from "@/components/EventPicker";
import { pushAlert } from "@/lib/alertHistory";
import axios from "axios";


const EVENT_SLUG = "davangere-members-day-2026";

function ReplayButton() {
  const [open, setOpen] = React.useState(false);
  const [promos, setPromos] = React.useState(null);
  const [running, setRunning] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [interval, setIntervalSec] = React.useState(6);
  const [soundtrack, setSoundtrack] = React.useState(() => {
    try { return localStorage.getItem("bni-replay-soundtrack") || "applause"; } catch { return "applause"; }
  });
  React.useEffect(() => { try { localStorage.setItem("bni-replay-soundtrack", soundtrack); } catch {} }, [soundtrack]);

  const SOUNDTRACK_OPTIONS = [
    { id: "applause", label: "Applause",            emoji: "👏", desc: "Standing-ovation crowd" },
    { id: "fanfare",  label: "Orchestral fanfare",  emoji: "🎺", desc: "Grand award ceremony" },
    { id: "dhol",     label: "Bollywood dhol",      emoji: "🥁", desc: "Festival energy" },
    { id: "silent",   label: "Silent",              emoji: "🔇", desc: "No soundtrack" },
  ];

  const load = React.useCallback(async () => {
    try {
      const r = await adminApi.get("/admin/big-screen/promotions/top", {
        params: { event_slug: EVENT_SLUG, limit: 10, scope: "today" },
      });
      setPromos(r.data.items || []);
      setRunning(!!r.data.running);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not load promotions"); }
  }, []);

  React.useEffect(() => { if (open) load(); }, [open, load]);

  const start = async () => {
    setBusy(true);
    try {
      const r = await adminApi.post("/admin/big-screen/replay/start", {
        event_slug: EVENT_SLUG, interval_seconds: interval, ttl_seconds: 5, scope: "today", limit: 10,
        soundtrack,
      });
      setRunning(true);
      toast.success(`Replay started · ${r.data.count} moments · ${soundtrack} · ~${Math.round(r.data.estimated_duration_seconds / 60)} min`, {
        icon: <Sparkles size={14} style={{ color: "var(--red-2)" }} />,
      });
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start replay");
    } finally { setBusy(false); }
  };

  const stop = async () => {
    setBusy(true);
    try {
      await adminApi.post("/admin/big-screen/replay/stop", null, { params: { event_slug: EVENT_SLUG } });
      setRunning(false);
      toast.info("Replay stopped");
    } catch (e) {
      toast.error("Could not stop replay");
    } finally { setBusy(false); }
  };

  return (
    <>
      <button
        data-testid="bigscreen-replay-btn"
        className="btn-ghost !py-1.5 !px-3 text-xs"
        onClick={() => setOpen(true)}
        title="Replay today's top 10 promoted referrals on the Big Screen"
      >
        <Sparkles size={12} /> Replay Top 10
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="!bg-[var(--panel)] !border-[var(--hairline)] !rounded-none max-w-[640px] p-0" data-testid="replay-dialog">
          <div className="p-6">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl !text-[var(--text)]">
                Closing applause · Top 10 replay
              </DialogTitle>
              <DialogDescription className="mono-label mt-1 !text-[var(--taupe)]">
                We'll light up the Big Screen with today's best referral moments, one every few seconds.
              </DialogDescription>
            </DialogHeader>

            <div className="mt-6 hairline p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="mono-label" style={{ color: "var(--taupe)" }}>Today's top {promos?.length || 0}</div>
                <label className="flex items-center gap-2 mono-label text-xs">
                  Interval
                  <select value={interval} onChange={(e) => setIntervalSec(Number(e.target.value))}
                    className="input-x !py-1 !px-2 !text-xs !w-24" data-testid="replay-interval">
                    <option value={4}>4s</option><option value={6}>6s</option><option value={8}>8s</option><option value={10}>10s</option>
                  </select>
                </label>
              </div>

              {/* Soundtrack picker */}
              <div className="mono-label mb-2" style={{ color: "var(--taupe)" }}>Soundtrack</div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4" data-testid="replay-soundtrack-options">
                {SOUNDTRACK_OPTIONS.map((o) => (
                  <button
                    key={o.id}
                    type="button"
                    data-testid={`replay-sfx-${o.id}`}
                    onClick={() => setSoundtrack(o.id)}
                    className="hairline p-3 text-left transition-colors"
                    style={{
                      background: soundtrack === o.id ? "rgba(226,62,71,0.08)" : "transparent",
                      borderColor: soundtrack === o.id ? "var(--red-2)" : "var(--hairline)",
                    }}
                  >
                    <div className="text-xl leading-none">{o.emoji}</div>
                    <div className="text-xs mt-1" style={{ color: soundtrack === o.id ? "var(--red-2)" : "var(--text)" }}>{o.label}</div>
                    <div className="mono-label mt-1" style={{ color: "var(--taupe)", fontSize: "9px" }}>{o.desc}</div>
                  </button>
                ))}
              </div>

              {!promos && <div className="mono-label py-4" style={{ color: "var(--taupe)" }}>Loading…</div>}
              {promos && promos.length === 0 && (
                <div className="mono-label py-4" style={{ color: "var(--taupe)" }} data-testid="replay-empty">
                  No referrals promoted yet today. Ask leaders to tap ✨ PROMOTE on their best referrals.
                </div>
              )}
              {promos && promos.length > 0 && (
                <ol className="space-y-2 max-h-[280px] overflow-y-auto pr-1" data-testid="replay-list">
                  {promos.map((p, i) => (
                    <li key={p.id} className="hairline px-3 py-2 flex items-center gap-3 text-xs">
                      <span className="font-serif text-base" style={{ color: "var(--red-2)" }}>#{i + 1}</span>
                      <div className="min-w-0 flex-1">
                        <div className="truncate">{p.message}</div>
                        <div className="mono-label truncate" style={{ color: "var(--taupe)" }}>
                          by {p.leader_name || "—"} · {new Date(p.promoted_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
                        </div>
                      </div>
                    </li>
                  ))}
                </ol>
              )}
            </div>

            <div className="mt-6 flex items-center gap-3 justify-end">
              <button className="btn-ghost" onClick={() => setOpen(false)}>Cancel</button>
              {running ? (
                <button data-testid="replay-stop" className="btn-ghost" disabled={busy} onClick={stop}>
                  <Square size={14} /> {busy ? "Stopping…" : "Stop replay"}
                </button>
              ) : (
                <button data-testid="replay-start" className="btn-red" disabled={busy || !promos?.length} onClick={start}>
                  <Play size={14} /> {busy ? "Starting…" : `Start replay (${promos?.length || 0})`}
                </button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function ActiveLeadersChip() {
  const [n, setN] = React.useState(0);
  const [names, setNames] = React.useState([]);
  React.useEffect(() => {
    const load = async () => {
      try {
        const t = localStorage.getItem("bni_admin_token");
        if (!t) return;
        const r = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/leader-sessions/active`,
          { params: { event_slug: "davangere-members-day-2026", window_min: 5 }, headers: { Authorization: `Bearer ${t}` } });
        setN(r.data.active_count || 0);
        setNames(r.data.recent_names || []);
      } catch {}
    };
    load(); const iv = setInterval(load, 30000); return () => clearInterval(iv);
  }, []);
  if (!n) return null;
  return (
    <span data-testid="active-leaders-chip" className="chip flex items-center gap-1.5"
      title={names.join(", ")}
      style={{ color: "var(--green)", borderColor: "var(--green)" }}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: "var(--green)", animation: "pulse 2s infinite" }} />
      {n} LEADER{n === 1 ? "" : "S"} ACTIVE
    </span>
  );
}
import { Toaster, toast } from "sonner";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AdminShell } from "@/components/AdminShell";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { AdminLogin, useAdminSession } from "@/pages/admin/AdminLogin";
import { BroadcastMonitor, useCampaigns } from "@/pages/admin/BroadcastMonitor";
import { WhatsAppLive } from "@/pages/admin/WhatsAppLive";
import { useDashboardStream } from "@/pages/admin/useDashboardStream";

const WA_REASONS = ["Invalid number", "Opted out", "Rate-limit exceeded", "Number changed"];
function buildWaRows() {
  return MEMBERS.slice(0, 60).map((m) => {
    const seed = m.id.split("-").pop();
    const n = Number(seed) || 1;
    const conf = ["read", "delivered", "sent", "read", "read", "failed"][n % 6];
    const qr   = ["read", "read", "delivered", "sent", "failed", "read"][(n * 3) % 6];
    const time = (h, mn) => `${String(h).padStart(2,"0")}:${String(mn).padStart(2,"0")}`;
    return {
      m,
      conf, confTs: time(9 + (n % 6), (n * 7) % 60), confReason: WA_REASONS[n % 4],
      qr,   qrTs:   time(10 + (n % 4), (n * 13) % 60), qrReason: WA_REASONS[(n * 3) % 4],
    };
  });
}

export default function AdminDashboard() {
  const { user, setUser, logout } = useAdminSession();
  if (user === null) return <AdminLogin onSuccess={setUser} />;
  if (!user || user.pending) return <AdminShell><div className="p-10 mono-label">Loading…</div></AdminShell>;
  return <AdminConsole user={user} onLogout={logout} />;
}

function AdminConsole({ user, onLogout }) {
  const { state, dispatch } = useDemo();
  const { funnel } = state;
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") || "dashboard";
  const setTab = (id) => setSearchParams(id === "dashboard" ? {} : { tab: id });
  const [tableFilter, setTableFilter] = useState("");
  const [chapterFilter, setChapterFilter] = useState("");
  const [filterOpts, setFilterOpts] = useState({ tables: [], chapters: [] });
  // Live referrals from backend (replaces DemoStore for the dashboard feed)
  const { items: referrals, aggregates: refAgg } = useReferralsLive({
    admin: true,
    table: tableFilter ? Number(tableFilter) : undefined,
    chapter: chapterFilter || undefined,
    intervalMs: 3000,
    limit: 60,
  });
  useEffect(() => {
    const t = localStorage.getItem("bni_admin_token");
    if (!t) return;
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/referrals/filters`,
      { params: { event_slug: "davangere-members-day-2026" }, headers: { Authorization: `Bearer ${t}` } })
      .then((r) => setFilterOpts(r.data)).catch(() => {});
  }, []);
  const { data: campaignData, reload: reloadCampaigns } = useCampaigns(30000);
  const { snap, live, lastTs } = useDashboardStream();
  const kpis = snap?.kpis; const fun = snap?.funnel; const tiers = snap?.tiers || []; const cats = snap?.categories || []; const regions = snap?.regions || [];
  const campaigns = campaignData?.campaigns;
  const pendingFailed = (campaigns || []).filter(c => c.template_key !== "otp").reduce((a, c) => a + c.failed, 0);
  const [alertDismissed, setAlertDismissed] = useState(false);

  const totalRegistered = state.funnel.paid;
  const paidCount = MEMBERS.filter(m => m.paid && !m.cancelled).length;
  const revenue = MEMBERS.filter(m => m.paid && !m.cancelled).reduce((a, m) => a + m.fee, 0) * (totalRegistered / Math.max(1, paidCount));
  const totalRefs = refAgg?.total_count ?? referrals.reduce((a, r) => a + r.count, 0);
  const totalValue = refAgg?.total_value_paise != null ? refAgg.total_value_paise / 100 : referrals.reduce((a, r) => a + r.value * r.count, 0);

  const byCategory = useMemo(() => {
    const counts = {};
    MEMBERS.forEach(m => { counts[m.category] = (counts[m.category] || 0) + 1; });
    return Object.entries(counts).sort((a,b)=>b[1]-a[1]);
  }, []);

  const byTier = useMemo(() => {
    const counts = {};
    MEMBERS.forEach(m => { counts[m.tier] = (counts[m.tier] || 0) + 1; });
    return Object.entries(counts);
  }, []);

  const topGivers = useMemo(() => {
    const map = {};
    referrals.forEach(r => { map[r.from] = (map[r.from] || 0) + r.count; });
    return Object.entries(map).sort((a,b)=>b[1]-a[1]).slice(0, 8);
  }, [referrals]);

  const tableTotals = useMemo(() => {
    const map = {};
    referrals.forEach(r => { const k = `t${r.table}`; map[k] = (map[k] || 0) + r.count; });
    return Object.entries(map).sort((a,b)=>b[1]-a[1]).slice(0, 6);
  }, [referrals]);

  // Live feed is fed directly by the polling hook — freshest first.
  const feed = referrals.slice(0, 12);

  // Persist sticky alerts to team-wide server-side history so every admin sees them.
  const lastEmittedRef = React.useRef(0);
  useEffect(() => {
    if (pendingFailed > 0 && !alertDismissed) {
      const now = Date.now();
      if (now - lastEmittedRef.current < 60_000) return;
      lastEmittedRef.current = now;
      pushAlert({
        type: "whatsapp_backlog",
        title: `${pendingFailed} WhatsApp messages still pending after retry`,
        detail: "Failed OR queued > threshold — review Broadcast Monitor",
        count: pendingFailed, severity: pendingFailed > 20 ? "high" : "medium",
      });
      import("@/lib/alertsApi").then(({ alertsApi }) => {
        alertsApi.emit({
          type: "whatsapp_backlog",
          title: `${pendingFailed} WhatsApp messages still pending after retry`,
          detail: "Failed OR queued > threshold — review Broadcast Monitor",
          count: pendingFailed, severity: pendingFailed > 20 ? "high" : "medium",
          dedupe_key: "whatsapp_backlog",
        }).catch(() => {});
      });
    }
  }, [pendingFailed, alertDismissed]);

  return (
    <AdminShell user={user} onLogout={onLogout} failedCount={pendingFailed}>
      <Toaster theme="dark" position="top-right" toastOptions={{
        style: { background: "var(--panel)", border: "1px solid var(--hairline)", color: "var(--text)", borderRadius: 0 }
      }} />
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-8">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <Eyebrow>Admin · Command Dashboard</Eyebrow>
              <h1 className="h-hero mt-3 text-4xl sm:text-5xl">Members Day, live.</h1>
            </div>
            <div className="flex items-center gap-3">
              <ReplayButton />
              <ActiveLeadersChip />
              <EventPicker />
              <AlertHistoryButton />
              <span data-testid="dashboard-live-badge"><LiveDot color={live === "live" ? "green" : "red"} label={live === "live" ? `LIVE · SSE${lastTs ? " · " + new Date(lastTs).toLocaleTimeString() : ""}` : live.toUpperCase()} /></span>
            </div>
          </div>

        {tab !== "dashboard" ? null : (<>
        {/* Payments Today — live ticker */}
        <div className="mt-8">
          <PaymentsTicker data={snap?.payments_today} />
        </div>

        {/* KPI row */}
        <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-0 hairline" style={{ borderRight: 0, borderBottom: 0 }}>
          <div className="border-b border-r border-[var(--hairline)]">
            <KpiBig k="Total Registered" v={kpis ? kpis.total_registered.toLocaleString("en-IN") : "—"} sub={kpis ? `Target ${kpis.registration_target.toLocaleString("en-IN")} · ${kpis.payment_pending} pending` : "Loading…"} pct={kpis ? Math.round(kpis.total_registered / kpis.registration_target * 100) : 0} />
          </div>
          <div className="border-b border-r border-[var(--hairline)]">
            <KpiBig k="Revenue Collected" v={kpis ? fmtInr(kpis.revenue_collected_paise) : "—"} sub={kpis ? `${kpis.paid_payments.toLocaleString("en-IN")} captured payments` : ""} accent="green" />
          </div>
          <div className="border-b border-r border-[var(--hairline)]">
            <KpiBig k="Meal Check-ins" v={kpis ? kpis.meal_checkins_today.toLocaleString("en-IN") : "—"} sub="Today · all windows" />
          </div>
          <div className="border-b border-[var(--hairline)]">
            <KpiBig k="Referrals Logged" v={kpis ? kpis.referrals_logged.toLocaleString("en-IN") : "—"} sub={kpis ? `${fmtInr(kpis.referral_value_paise)} business volume` : ""} accent="red" />
          </div>
        </div>

        {/* Row 2: Funnel + Tier */}
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-6">
          <Panel className="p-6 lg:col-span-7">
            <div className="flex items-center justify-between">
              <div>
                <MonoLabel>Registration & Payment Funnel</MonoLabel>
                <div className="font-serif text-2xl mt-2">Visit → Paid</div>
              </div>
              <span className="chip red" data-testid="funnel-conv">CONV. {fun && fun.landing_visits ? (fun.confirmed / fun.landing_visits * 100).toFixed(1) : "0.0"}%</span>
            </div>
            <div className="mt-5 space-y-3" data-testid="funnel-rows">
              {fun ? (() => { const mx = Math.max(fun.landing_visits, fun.form_started, fun.payment_initiated, fun.confirmed, fun.cancelled, 1); return (<>
                <FunnelRow label="Landing visits"       n={fun.landing_visits} max={mx} />
                <FunnelRow label="Form started"         n={fun.form_started} max={mx} />
                <FunnelRow label="Payment initiated"    n={fun.payment_initiated} max={mx} />
                <FunnelRow label="Confirmed pass"       n={fun.confirmed} max={mx} accent="green" />
                <FunnelRow label="Cancelled / abandoned" n={fun.cancelled} max={mx} accent="red" />
              </>); })() : <div className="mono-label">Loading…</div>}
            </div>
          </Panel>

          <Panel className="p-6 lg:col-span-5">
            <MonoLabel>Registration by Tier</MonoLabel>
            <div className="mt-5 space-y-4" data-testid="tier-breakdown">
              {tiers.map((t) => {
                const mx = Math.max(...tiers.map((x) => x.count), 1);
                return (
                  <div key={t.tier_id} className="hairline-b pb-3 last:border-none last:pb-0" data-testid={`tier-row-${t.tier_id}`}>
                    <div className="flex justify-between text-sm">
                      <span>{t.name}</span>
                      <span className="font-mono">{t.count} · {fmtInr(t.revenue_paise)}</span>
                    </div>
                    <div className="mt-2 h-1.5 bg-[var(--panel-2)]">
                      <div className="h-full" style={{ background: "var(--red-2)", width: `${t.count / mx * 100}%` }} />
                    </div>
                  </div>
                );
              })}
              {snap && tiers.length === 0 && <div className="mono-label">No tiers</div>}
            </div>
          </Panel>
        </div>

        {/* Row 3: Categories + WhatsApp */}
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-6">
          <Panel className="p-6 lg:col-span-7">
            <MonoLabel>Registrations by Category</MonoLabel>
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3" data-testid="category-breakdown">
              {snap && cats.length === 0 && <div className="mono-label">No paid registrations yet</div>}
              {cats.map(({ category: c, count: n }) => (
                <div key={c}>
                  <div className="flex justify-between text-sm">
                    <span className="truncate">{c}</span>
                    <span className="font-mono">{n}</span>
                  </div>
                  <div className="mt-1 h-1 bg-[var(--panel-2)]">
                    <div className="h-full" style={{ background: "var(--red)", width: `${n / (cats[0]?.count || 1) * 100}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </Panel>

          <BroadcastMonitor campaigns={campaigns} reload={reloadCampaigns} />
        </div>

        {/* Row 3b: Regions (full-width) */}
        <div className="mt-6">
          <Panel className="p-6">
            <MonoLabel>Registrations by Region</MonoLabel>
            <RegionBreakdown regions={regions} loaded={!!snap} />
          </Panel>
        </div>

        {/* Row 4: Leaderboard + Live feed */}
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-6">
          <Panel className="p-6 lg:col-span-7">
            <div className="flex items-center justify-between">
              <MonoLabel>Conclave Leaderboard · Top Givers</MonoLabel>
              <Award size={14} style={{ color: "var(--amber)" }} />
            </div>
            <div className="mt-4">
              {topGivers.map(([id, n], i) => {
                const m = findMember(id);
                if (!m) return null;
                const regId = m.reg_id || id;
                return (
                  <button
                    key={id}
                    data-testid={`spotlight-member-${regId}`}
                    onClick={async () => {
                      try {
                        await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/admin/bigscreen/spotlight`,
                          { event_slug: "davangere-members-day-2026", registration_id: regId, note: `${n} referrals · top giver` },
                          { headers: { Authorization: `Bearer ${localStorage.getItem("bni_admin_token")}` } });
                        toast.success(`${m.name.split(" ")[0]} is now on the Big Screen`);
                      } catch (e) { toast.error(e?.response?.data?.detail || "Spotlight failed"); }
                    }}
                    className="w-full text-left flex items-center gap-3 hairline p-3 hover:border-[var(--red)] transition-colors" style={{ marginTop: i === 0 ? 0 : -1 }}>
                    <div className="w-8 text-center font-serif text-xl" style={{ color: i < 3 ? "var(--red-2)" : "var(--taupe)" }}>{i+1}</div>
                    <img src={m.avatar} className="w-10 h-10 object-cover border border-[var(--hairline)]" alt="" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm truncate">{m.name}</div>
                      <div className="mono-label truncate" style={{ color: "var(--taupe)" }}>{m.chapter} · {m.category}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-serif text-xl">{n}</div>
                      <div className="mono-label" style={{ color: "var(--taupe)" }}>REFS · SPOTLIGHT</div>
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="mt-6">
              <MonoLabel>Top Tables (by referrals) · tap to spotlight on Big Screen</MonoLabel>
              <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-3">
                {tableTotals.map(([k, n]) => {
                  const tableNum = Number(k.slice(1));
                  return (
                    <button
                      key={k}
                      data-testid={`spotlight-${tableNum}`}
                      onClick={async () => {
                        try {
                          await setBigScreenSpotlight(tableNum, `${n} referrals · trending`);
                          toast.success(`Table #${tableNum} is now on the Big Screen`);
                        } catch (e) { toast.error(e?.response?.data?.detail || "Spotlight failed"); }
                      }}
                      className="hairline p-3 text-left hover:border-[var(--red)] transition-colors"
                    >
                      <div className="mono-label">Table</div>
                      <div className="font-serif text-2xl mt-1">#{k.slice(1)}</div>
                      <div className="mono-label mt-1" style={{ color: "var(--red-2)" }}>{n} REFS · SPOTLIGHT</div>
                    </button>
                  );
                })}
                {tableTotals.length > 0 && (
                  <button
                    data-testid="spotlight-clear"
                    onClick={async () => {
                      try { await setBigScreenSpotlight(null, null); toast.success("Big Screen spotlight cleared"); }
                      catch (e) { toast.error(e?.response?.data?.detail || "Clear failed"); }
                    }}
                    className="hairline p-3 text-left"
                    style={{ borderStyle: "dashed", opacity: 0.7 }}
                  >
                    <div className="mono-label">Clear spotlight</div>
                    <div className="text-sm mt-2" style={{ color: "var(--taupe)" }}>Return the podium to the leaderboard.</div>
                  </button>
                )}
              </div>
            </div>
          </Panel>

          <Panel className="p-6 lg:col-span-5">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <MonoLabel>Live Referral Feed{(tableFilter || chapterFilter) ? ` · ${referrals.length} filtered` : ""}</MonoLabel>
              <LiveDot />
            </div>
            <div className="mt-3 flex flex-wrap gap-2" data-testid="feed-filters">
              <select data-testid="feed-table-filter" className="input-x !py-1.5 !w-auto text-xs" value={tableFilter} onChange={(e) => setTableFilter(e.target.value)}>
                <option value="">All tables</option>
                {filterOpts.tables.map((t) => <option key={t} value={t}>Table {t}</option>)}
              </select>
              <select data-testid="feed-chapter-filter" className="input-x !py-1.5 !w-auto text-xs" value={chapterFilter} onChange={(e) => setChapterFilter(e.target.value)}>
                <option value="">All chapters</option>
                {filterOpts.chapters.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              {(tableFilter || chapterFilter) && (
                <button data-testid="feed-clear-filter" onClick={() => { setTableFilter(""); setChapterFilter(""); }} className="btn-ghost !py-1.5 !text-xs">Clear</button>
              )}
            </div>
            <div className="mt-4 max-h-[520px] overflow-auto pr-1">
              <AnimatePresence initial={false}>
                {feed.length === 0 && <div className="mono-label py-6 text-center" style={{ color: "var(--taupe)" }}>No referrals match this filter</div>}
                {feed.map((r) => {
                  const fMeta = r.fromMeta || {}; const tMeta = r.toMeta || {};
                  const f = findMember(r.from) || { name: fMeta.name || r.from, chapter: fMeta.chapter || "—", avatar: "/bni-logo.png" };
                  const t = findMember(r.to) || { name: tMeta.name || r.to, chapter: tMeta.chapter || "—", avatar: "/bni-logo.png" };
                  return (
                    <motion.div
                      key={r.id}
                      initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }}
                      className="hairline-b py-3 flex items-center gap-3"
                      data-testid={`feed-row-${r.id}`}
                    >
                      <img src={f.avatar || "/bni-logo.png"} className="w-8 h-8 object-cover border border-[var(--hairline)]" alt="" />
                      <ChevronsRight size={14} style={{ color: "var(--red-2)" }} />
                      <img src={t.avatar || "/bni-logo.png"} className="w-8 h-8 object-cover border border-[var(--hairline)]" alt="" />
                      <div className="flex-1 min-w-0">
                        <div className="text-xs truncate">{(f.name || r.from).split(' ')[0]} → {(t.name || r.to).split(' ')[0]}</div>
                        <div className="mono-label" style={{ color: "var(--taupe)" }}>Table #{r.table} · R{r.round}</div>
                      </div>
                      <div className="text-right">
                        <div className="mono-label" style={{ color: "var(--red)" }}>+{r.count}</div>
                        <div className="mono-label" style={{ color: "var(--taupe)" }}>₹{(r.value/1000).toFixed(0)}k</div>
                        {r.logged_by_name && <div className="mono-label mt-1" style={{ color: "var(--muted)" }} title={r.logged_by_phone || ""}>by {r.logged_by_name.split(' ')[0]}</div>}
                      </div>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>
          </Panel>
        </div>
        </>)}

        {tab === "whatsapp" && <WhatsAppLive campaigns={campaigns} reloadCampaigns={reloadCampaigns} />}
        {tab === "settings" && <SettingsTab />}
        </div>

      <StickyAlert
        count={pendingFailed}
        dismissed={alertDismissed}
        onReview={() => { setTab("whatsapp"); setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 60); }}
        onDismiss={() => setAlertDismissed(true)}
      />
    </AdminShell>
  );
}

const fmtInr = (paise) => {
  const r = paise / 100;
  if (r >= 1e7) return `₹${(r / 1e7).toFixed(2)}Cr`;
  if (r >= 1e5) return `₹${(r / 1e5).toFixed(1)}L`;
  return `₹${r.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
};

// -------------- Sticky bottom alert --------------
function StickyAlert({ count, dismissed, onReview, onDismiss }) {
  if (dismissed || count <= 0) return null;
  return (
    <div className="admin-sticky-alert" data-testid="admin-sticky-alert">
      <div className="admin-sticky-alert-inner">
        <div className="left">
          <span className="pulse" />
          <AlertTriangle size={14} style={{ color: "var(--red-2)" }} />
          <span className="msg">
            <strong>{count}</strong> WhatsApp {count === 1 ? "message" : "messages"} still pending after retry
          </span>
        </div>
        <div className="right">
          <button data-testid="sticky-alert-review" onClick={onReview} className="btn-red !py-1.5 !px-3 text-[12px]">
            Review
          </button>
          <button data-testid="sticky-alert-dismiss" onClick={onDismiss} className="dismiss" aria-label="Dismiss">
            <X size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}

// -------------- WhatsApp per-member view --------------
function WhatsAppTab({ waTemplates, dispatch, onPendingChange }) {
  const [q, setQ] = useState("");
  const [rowStatus, setRowStatus] = useState({});
  const [failedFor, setFailedFor] = useState(null); // "conf" | "qr" | null

  const rows = useMemo(() => buildWaRows(), []);

  // Report current pending failed count upstream (for tab badge + sticky alert)
  useEffect(() => {
    const pending = rows.reduce((acc, r) => {
      const cs = rowStatus[`${r.m.id}-conf`] || r.conf;
      const qs = rowStatus[`${r.m.id}-qr`] || r.qr;
      return acc + (cs === "failed" ? 1 : 0) + (qs === "failed" ? 1 : 0);
    }, 0);
    onPendingChange?.(pending);
  }, [rows, rowStatus, onPendingChange]);

  const filtered = rows.filter((r) =>
    !q || r.m.name.toLowerCase().includes(q.toLowerCase()) ||
    r.m.id.toLowerCase().includes(q.toLowerCase()) ||
    r.m.phone.includes(q) || r.m.chapter.toLowerCase().includes(q.toLowerCase())
  );

  const failedRows = (type) => rows.filter((r) => (type === "conf" ? r.conf : r.qr) === "failed");

  const retrig = (memberId, type) => {
    setRowStatus((s) => ({ ...s, [`${memberId}-${type}`]: "sent-now" }));
    dispatch({ type: "RETRIGGER_WA", id: type === "conf" ? "wa-conf" : "wa-qr" });
    toast.success(`Retriggered ${type === "conf" ? "Registration Confirmation" : "QR Code Pass"} → ${findMember(memberId).name.split(" ")[0]}`);
    // After the "sending" state clears, mark as sent (persists, so pending count drops)
    setTimeout(() => setRowStatus((s) => ({ ...s, [`${memberId}-${type}`]: "sent" })), 1600);
  };

  const retrigAllFailed = (type) => {
    const list = failedRows(type);
    list.forEach((r, i) => setTimeout(() => retrig(r.m.id, type), i * 120));
    toast.success(`Retriggering ${list.length} failed ${type === "conf" ? "confirmations" : "QR passes"}…`);
  };

  return (
    <div className="mt-6 space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {waTemplates.filter(t => t.id === "wa-conf" || t.id === "wa-qr").map((t) => {
          const typeKey = t.id === "wa-conf" ? "conf" : "qr";
          return (
            <Panel key={t.id} className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <MonoLabel>{t.name}</MonoLabel>
                  <div className="font-serif text-2xl mt-2">{t.sent.toLocaleString("en-IN")} <span className="text-sm" style={{ color: "var(--taupe)" }}>sent</span></div>
                </div>
                <MessageCircle size={22} style={{ color: "var(--green)" }} />
              </div>
              <div className="mt-4 grid grid-cols-4 gap-3">
                <WaCell k="Delivered" v={t.delivered} c="green" />
                <WaCell k="Read"      v={t.read}      c="green" />
                <button
                  data-testid={`wa-failed-open-${typeKey}`}
                  onClick={() => setFailedFor(typeKey)}
                  className="hairline p-3 text-left hover:border-[var(--red)] transition-colors cursor-pointer"
                >
                  <div className="mono-label flex items-center gap-1">Failed <ArrowUpRight size={10} style={{ color: "var(--red-2)" }} /></div>
                  <div className="font-mono text-lg mt-1" style={{ color: "var(--red-2)" }}>{t.failed.toLocaleString("en-IN")}</div>
                </button>
                <WaCell k="Rate"      v={`${Math.round(t.read/t.sent*100)}%`} c="green" />
              </div>
            </Panel>
          );
        })}
      </div>

      <Panel className="p-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <MonoLabel>Per-Member Message Log</MonoLabel>
            <div className="font-serif text-xl mt-1">{filtered.length} members</div>
          </div>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--taupe)" }} />
            <input data-testid="wa-search" className="input-x !pl-9 !w-72" placeholder="Search name, ID, phone, chapter…" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
        </div>

        <div className="mt-5 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left mono-label" style={{ color: "var(--taupe)" }}>
                <th className="py-3 pr-3">Member</th>
                <th className="py-3 pr-3">Registration Confirmation</th>
                <th className="py-3 pr-3">QR Code Pass</th>
                <th className="py-3 text-right">Retrigger</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.m.id} className="hairline-t">
                  <td className="py-3 pr-3">
                    <div className="flex items-center gap-3">
                      <img src={r.m.avatar} alt="" className="w-9 h-9 object-cover border border-[var(--hairline)]" />
                      <div className="min-w-0">
                        <div className="text-sm truncate">{r.m.name}</div>
                        <div className="mono-label truncate" style={{ color: "var(--taupe)" }}>{r.m.id} · {r.m.chapter}</div>
                      </div>
                    </div>
                  </td>
                  <td className="py-3 pr-3"><WaBadge status={rowStatus[`${r.m.id}-conf`] || r.conf} ts={r.confTs} /></td>
                  <td className="py-3 pr-3"><WaBadge status={rowStatus[`${r.m.id}-qr`] || r.qr} ts={r.qrTs} /></td>
                  <td className="py-3 text-right">
                    <div className="inline-flex items-center gap-2">
                      <button data-testid={`wa-retrigger-conf-${r.m.id}`} onClick={() => retrig(r.m.id, "conf")} className="btn-ghost !py-1.5 !px-2 text-[11px]"><Send size={11} /> Conf</button>
                      <button data-testid={`wa-retrigger-qr-${r.m.id}`} onClick={() => retrig(r.m.id, "qr")} className="btn-ghost !py-1.5 !px-2 text-[11px]"><Send size={11} /> QR</button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={4} className="py-8 text-center mono-label" style={{ color: "var(--muted)" }}>No members match your search</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Panel>

      <FailedModal type={failedFor} rows={failedFor ? failedRows(failedFor) : []} rowStatus={rowStatus} onClose={() => setFailedFor(null)} onRetrig={retrig} onRetrigAll={retrigAllFailed} />
    </div>
  );
}

function FailedModal({ type, rows, rowStatus, onClose, onRetrig, onRetrigAll }) {
  const title = type === "conf" ? "Failed · Registration Confirmation" : "Failed · QR Code Pass";
  const [confirming, setConfirming] = useState(false);
  useEffect(() => { if (!type) setConfirming(false); }, [type]);
  return (
    <AnimatePresence>
      {type && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] bg-black/75 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
            className="w-full max-w-[720px]"
            onClick={(e) => e.stopPropagation()}
            data-testid="wa-failed-modal"
          >
            <Panel className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <div className="chip red inline-flex items-center gap-1"><AlertTriangle size={11} /> {rows.length} FAILED</div>
                  <div className="font-serif text-xl mt-2">{title}</div>
                  <div className="mono-label mt-1" style={{ color: "var(--taupe)" }}>Retrigger sends the message via WhatsApp Business API (mocked)</div>
                </div>
                <button data-testid="wa-failed-close" onClick={onClose}><X size={18} /></button>
              </div>

              {confirming ? (
                <div className="mt-4 hairline p-4" style={{ background: "rgba(204,37,47,0.08)", borderColor: "var(--red-2)" }} data-testid="wa-failed-confirm">
                  <div className="flex items-start gap-3">
                    <AlertTriangle size={16} style={{ color: "var(--red-2)", marginTop: 2 }} />
                    <div className="flex-1">
                      <div className="text-sm">Retrigger <strong>{rows.length}</strong> {rows.length === 1 ? "message" : "messages"}?</div>
                      <div className="mono-label mt-1" style={{ color: "var(--taupe)" }}>This will re-send the {type === "conf" ? "Registration Confirmation" : "QR Code Pass"} template to every failed recipient. This action cannot be undone.</div>
                    </div>
                  </div>
                  <div className="mt-4 flex items-center justify-end gap-2">
                    <button data-testid="wa-failed-confirm-cancel" onClick={() => setConfirming(false)} className="btn-ghost !py-2 !px-3 text-[12px]">Cancel</button>
                    <button
                      data-testid="wa-failed-confirm-yes"
                      onClick={() => { setConfirming(false); onRetrigAll(type); }}
                      className="btn-red !py-2 !px-3 text-[12px]"
                    >
                      <Send size={12} /> Yes, retrigger {rows.length}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="mt-4 flex items-center justify-between">
                  <MonoLabel>Members</MonoLabel>
                  <button
                    data-testid="wa-failed-retrigger-all"
                    onClick={() => setConfirming(true)}
                    disabled={rows.length === 0}
                    className="btn-red !py-2 !px-3 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <Send size={12} /> Retrigger all
                  </button>
                </div>
              )}

              <div className="mt-3 max-h-[420px] overflow-auto pr-1">
                {rows.length === 0 && (
                  <div className="py-8 text-center mono-label" style={{ color: "var(--muted)" }}>No failed messages 🎉</div>
                )}
                {rows.map((r) => {
                  const st = rowStatus[`${r.m.id}-${type}`];
                  const ts = type === "conf" ? r.confTs : r.qrTs;
                  const reason = type === "conf" ? r.confReason : r.qrReason;
                  const resolved = st === "sent" || st === "sent-now";
                  return (
                    <div key={r.m.id} className="hairline-b py-3 flex items-center gap-3">
                      <img src={r.m.avatar} alt="" className="w-10 h-10 object-cover border border-[var(--hairline)]" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm truncate">{r.m.name}</div>
                        <div className="mono-label truncate" style={{ color: "var(--taupe)" }}>{r.m.id} · {r.m.chapter} · {r.m.phone}</div>
                        {!resolved && (
                          <div className="mono-label truncate mt-1" data-testid={`wa-failed-reason-${r.m.id}`} style={{ color: "var(--red-2)" }}>
                            Reason · {reason}
                          </div>
                        )}
                      </div>
                      <WaBadge status={st || "failed"} ts={!st ? ts : null} />
                      <button
                        data-testid={`wa-failed-retrigger-${r.m.id}`}
                        onClick={() => onRetrig(r.m.id, type)}
                        disabled={resolved}
                        className="btn-ghost !py-1.5 !px-2 text-[11px] disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <Send size={11} /> Retrigger
                      </button>
                    </div>
                  );
                })}
              </div>
            </Panel>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function WaBadge({ status, ts }) {
  const map = {
    "sent-now":  { c: "var(--red-2)", i: Send,          l: "SENDING…" },
    "sent":      { c: "var(--text)",  i: Send,          l: "SENT" },
    "delivered": { c: "var(--body)",  i: Check,         l: "DELIVERED" },
    "read":      { c: "var(--green)", i: Check,         l: "READ" },
    "failed":    { c: "var(--red-2)", i: AlertTriangle, l: "FAILED" },
  };
  const S = map[status] || map.sent;
  return (
    <div className="inline-flex items-center gap-2">
      <S.i size={12} style={{ color: S.c }} />
      <span className="mono-label" style={{ color: S.c }}>{S.l}</span>
      {ts && <span className="mono-label" style={{ color: "var(--muted)" }}>· {ts}</span>}
    </div>
  );
}

function WaCell({ k, v, c }) {
  const col = c === "green" ? "var(--green)" : c === "red" ? "var(--red-2)" : "var(--text)";
  return (
    <div className="hairline p-3">
      <div className="mono-label">{k}</div>
      <div className="font-mono text-lg mt-1" style={{ color: col }}>{typeof v === "number" ? v.toLocaleString("en-IN") : v}</div>
    </div>
  );
}

// -------------- Settings tab (Table Leaders only) --------------
function SettingsTab() {
  return (
    <div className="mt-6 space-y-6">
      <TableLeadersConfig />
    </div>
  );
}

// -------------- Table Leaders configuration --------------
function TableLeadersConfig() {
  const STORAGE = "bni-md-table-leaders-v1";
  const TABLES = 50;
  // Default leader = first seated member of Round 1 at that table
  const defaults = useMemo(() => {
    const map = {};
    for (let t = 1; t <= TABLES; t++) {
      // Import lazily to avoid circular — SEATING is exported from seed
      // eslint-disable-next-line global-require
      const { SEATING } = require("@/data/seed");
      const first = SEATING?.[1]?.[t - 1]?.seats?.[0]?.memberId;
      if (first) map[t] = first;
    }
    return map;
  }, []);
  const [leaders, setLeaders] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE);
      if (raw) return { ...defaults, ...JSON.parse(raw) };
    } catch {}
    return defaults;
  });
  const [query, setQuery] = useState("");
  const [pickerFor, setPickerFor] = useState(null); // tableNo
  const [pickerQ, setPickerQ] = useState("");

  useEffect(() => {
    try { localStorage.setItem(STORAGE, JSON.stringify(leaders)); } catch {}
  }, [leaders]);

  const rows = Array.from({ length: TABLES }, (_, i) => i + 1).filter((t) => {
    if (!query) return true;
    const m = findMember(leaders[t]);
    return String(t).includes(query) || (m && (m.name.toLowerCase().includes(query.toLowerCase()) || m.chapter.toLowerCase().includes(query.toLowerCase())));
  });

  const candidates = MEMBERS.filter((m) =>
    !pickerQ ||
    m.name.toLowerCase().includes(pickerQ.toLowerCase()) ||
    m.id.toLowerCase().includes(pickerQ.toLowerCase()) ||
    m.chapter.toLowerCase().includes(pickerQ.toLowerCase())
  ).slice(0, 30);

  const assign = (tableNo, memberId) => {
    setLeaders((s) => ({ ...s, [tableNo]: memberId }));
    setPickerFor(null);
    setPickerQ("");
    toast.success(`Table #${tableNo} → ${findMember(memberId)?.name || memberId}`);
  };

  return (
    <Panel className="p-6" data-testid="table-leaders-config">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <MonoLabel>Table Leaders</MonoLabel>
          <div className="font-serif text-2xl mt-2">Assign a leader for each of 50 tables</div>
          <div className="mono-label mt-2" style={{ color: "var(--taupe)" }}>Changes save automatically. Leader gets referral-logging privilege at their table.</div>
        </div>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--taupe)" }} />
          <input
            data-testid="tl-search"
            className="input-x !pl-9 !w-72"
            placeholder="Filter by table # or leader name…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
      </div>

      <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-0">
        {rows.map((t, i) => {
          const m = findMember(leaders[t]);
          return (
            <div
              key={t}
              className={`hairline p-3 flex items-center gap-3 ${i % 2 === 1 ? "md:border-l-0" : ""}`}
              style={{ marginTop: i > 1 ? -1 : 0, marginLeft: i % 2 === 1 ? -1 : 0 }}
              data-testid={`tl-row-${t}`}
            >
              <div className="w-14 text-center">
                <div className="mono-label">Table</div>
                <div className="font-serif text-xl leading-none mt-1">#{t}</div>
              </div>
              {m ? (
                <img src={m.avatar} alt="" className="w-10 h-10 object-cover border border-[var(--hairline)]" />
              ) : (
                <div className="w-10 h-10 hairline flex items-center justify-center mono-label" style={{ color: "var(--muted)" }}>—</div>
              )}
              <div className="flex-1 min-w-0">
                <div className="text-sm truncate">{m?.name || "Unassigned"}</div>
                <div className="mono-label truncate" style={{ color: "var(--taupe)" }}>{m ? `${m.chapter} · ${m.category}` : "Choose a leader"}</div>
              </div>
              <button
                data-testid={`tl-change-${t}`}
                onClick={() => { setPickerFor(t); setPickerQ(""); }}
                className="btn-ghost !py-1.5 !px-2 text-[11px]"
              >
                Change
              </button>
            </div>
          );
        })}
      </div>

      <AnimatePresence>
        {pickerFor !== null && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setPickerFor(null)}
          >
            <motion.div
              initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
              className="w-full max-w-[520px]"
              onClick={(e) => e.stopPropagation()}
              data-testid="tl-picker"
            >
              <Panel className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <MonoLabel>Assign Leader</MonoLabel>
                    <div className="font-serif text-xl mt-1">Table #{pickerFor}</div>
                  </div>
                  <button data-testid="tl-picker-close" onClick={() => setPickerFor(null)}><X size={18} /></button>
                </div>
                <div className="relative mt-4">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--taupe)" }} />
                  <input
                    data-testid="tl-picker-search"
                    className="input-x !pl-9"
                    placeholder="Search 300 members by name, ID or chapter…"
                    autoFocus
                    value={pickerQ}
                    onChange={(e) => setPickerQ(e.target.value)}
                  />
                </div>
                <div className="mt-3 max-h-[380px] overflow-auto pr-1">
                  {candidates.map((m) => (
                    <button
                      key={m.id}
                      data-testid={`tl-pick-${m.id}`}
                      onClick={() => assign(pickerFor, m.id)}
                      className="w-full flex items-center gap-3 py-2 px-2 hairline-b hover:bg-[var(--panel-2)] text-left"
                    >
                      <img src={m.avatar} alt="" className="w-9 h-9 object-cover border border-[var(--hairline)]" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm truncate">{m.name}</div>
                        <div className="mono-label truncate" style={{ color: "var(--taupe)" }}>{m.id} · {m.chapter}</div>
                      </div>
                      <span className="mono-label" style={{ color: "var(--red-2)" }}>SELECT</span>
                    </button>
                  ))}
                  {candidates.length === 0 && (
                    <div className="py-8 text-center mono-label" style={{ color: "var(--muted)" }}>No members match</div>
                  )}
                </div>
              </Panel>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </Panel>
  );
}

// -------------- Payments Today ticker --------------
function PaymentsTicker({ data }) {
  const count = data?.count ?? 0;
  const paise = data?.paise ?? 0;
  const last = data?.last || null;
  const prevCountRef = React.useRef(count);
  const prevPaiseRef = React.useRef(paise);
  const [pulse, setPulse] = React.useState(false);

  React.useEffect(() => {
    if (count > prevCountRef.current || paise > prevPaiseRef.current) {
      setPulse(true);
      const t = setTimeout(() => setPulse(false), 1500);
      prevCountRef.current = count;
      prevPaiseRef.current = paise;
      return () => clearTimeout(t);
    }
    prevCountRef.current = count;
    prevPaiseRef.current = paise;
  }, [count, paise]);

  const fmtFull = (p) => `₹${(p / 100).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
  const relTime = (iso) => {
    if (!iso) return "";
    const diff = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
    if (diff < 60) return "Just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div className={`payments-ticker ${pulse ? "pulse" : ""}`} data-testid="payments-today-ticker">
      <div className="pt-left">
        <div className="pt-eyebrow">
          <span className="dot" />
          <span>Payments today · Razorpay LIVE</span>
        </div>
        <div className="pt-amount" data-testid="payments-today-amount">
          {data ? fmtFull(paise) : "—"}
        </div>
        <div className="pt-sub">
          <span className="count-badge" data-testid="payments-today-count">{count} CAPTURED</span>
          <span className="sep">·</span>
          <span>Today · Asia/Kolkata</span>
          <span className="sep">·</span>
          <span>Updates live every 5s</span>
        </div>
      </div>
      <div className="pt-right">
        {last ? (
          <>
            <div className="pt-last-label">Latest payment</div>
            <div className="pt-last-amt" data-testid="payments-today-last-amt">{fmtFull(last.amount_paise)}</div>
            <div className="pt-last-who" data-testid="payments-today-last-who">
              {last.name}{last.chapter ? ` · ${last.chapter}` : ""}
            </div>
            <div className="pt-last-when">{relTime(last.captured_at)}</div>
          </>
        ) : (
          <div className="pt-empty">No payments yet today</div>
        )}
      </div>
    </div>
  );
}

function KpiBig({ k, v, sub, pct, accent }) {
  const col = accent === "red" ? "var(--red-2)" : accent === "green" ? "var(--green)" : "var(--text)";
  return (
    <div className="p-6">
      <div className="mono-label">{k}</div>
      <div className="font-serif text-3xl sm:text-4xl mt-3" style={{ color: col }}>{v}</div>
      {typeof pct === "number" && (
        <div className="mt-3 h-1 bg-[var(--panel-2)]"><div className="h-full" style={{ background: "var(--red)", width: `${Math.min(100,pct)}%` }} /></div>
      )}
      {sub && <div className="mono-label mt-2" style={{ color: "var(--taupe)" }}>{sub}</div>}
    </div>
  );
}

function FunnelRow({ label, n, max, accent }) {
  const w = (n / max) * 100;
  const col = accent === "green" ? "var(--green)" : accent === "red" ? "var(--red)" : "var(--red-2)";
  return (
    <div>
      <div className="flex justify-between text-sm">
        <span>{label}</span>
        <span className="font-mono">{n.toLocaleString("en-IN")}</span>
      </div>
      <div className="mt-1.5 h-2 bg-[var(--panel-2)]">
        <div className="h-full" style={{ background: col, width: `${w}%` }} />
      </div>
    </div>
  );
}

function WaStat({ k, v, c }) {
  const col = c === "green" ? "var(--green)" : c === "red" ? "var(--red-2)" : "var(--text)";
  return (
    <div>
      <div className="mono-label" style={{ fontSize: 9 }}>{k}</div>
      <div className="font-mono text-sm mt-1" style={{ color: col }}>{v.toLocaleString("en-IN")}</div>
    </div>
  );
}


function RegionBreakdown({ regions, loaded }) {
  // Animate progress bars + counts on first load
  const [progress, setProgress] = useState(0);
  const [animatedCounts, setAnimatedCounts] = useState({});

  const sorted = useMemo(
    () => [...(regions || [])].sort((a, b) => b.count - a.count),
    [regions]
  );
  const maxCount = Math.max(1, ...(sorted.map((r) => r.count) || [1]));
  const totalPaid = sorted.reduce((a, r) => a + r.count, 0);
  const key = sorted.map((r) => `${r.region}:${r.count}`).join("|");

  useEffect(() => {
    if (!sorted.length) return;
    setProgress(0);
    setAnimatedCounts({});
    const start = performance.now();
    const dur = 900;
    let raf;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / dur);
      // ease-out
      const eased = 1 - Math.pow(1 - t, 3);
      setProgress(eased);
      const next = {};
      sorted.forEach((r) => { next[r.region] = Math.round(r.count * eased); });
      setAnimatedCounts(next);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  if (!loaded) {
    return <div className="mono-label mt-5" style={{ color: "var(--taupe)" }}>Loading…</div>;
  }
  if (!sorted.length) {
    return <div className="mono-label mt-5" style={{ color: "var(--taupe)" }}>No paid registrations yet</div>;
  }

  return (
    <div className="mt-5" data-testid="region-breakdown">
      <div className="flex items-baseline justify-between mb-4">
        <span className="mono-label" style={{ color: "var(--taupe)" }}>
          {sorted.length} {sorted.length === 1 ? "region" : "regions"}
        </span>
        <span className="font-mono text-sm">
          {Math.round(totalPaid * progress).toLocaleString("en-IN")} paid
        </span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
        {sorted.map((r) => {
          const widthPct = (r.count / maxCount) * 100 * progress;
          const shownCount = animatedCounts[r.region] ?? 0;
          return (
            <div key={r.region} data-testid={`region-row-${r.region}`}>
              <div className="flex justify-between text-sm">
                <span className="truncate" title={r.region}>{r.region}</span>
                <span className="font-mono">{shownCount.toLocaleString("en-IN")}</span>
              </div>
              <div className="mt-1.5 h-1.5 bg-[var(--panel-2)]">
                <div
                  className="h-full"
                  style={{
                    background: "var(--red-2)",
                    width: `${widthPct}%`,
                    transition: "width 60ms linear",
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
